import { CanonicalPresentationModelV1Schema, } from "../contracts/canonical-presentation.js";
import { ChangeRequestStatusSchema, } from "../contracts/approval-change.js";
import { stableStringify } from "../hashing/canonical-json.js";
const PROHIBITED_CLIENT_TERMS = [
    "runId",
    "artifactSetHash",
    "exportBundleHash",
    "businessGraphRevisionId",
    "Quality Gate",
    "worker",
    "tenant",
    "schema",
    "GenerationRun",
];
function parseStatus(raw) {
    const parsed = ChangeRequestStatusSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
function idsAgree(input) {
    return Boolean(input.projectId.trim() && input.changeRequestId.trim() && input.createdAt.trim());
}
function hasCompleteMatchingResolution(input) {
    return (input.status === "RESOLVED" &&
        Boolean(input.resolvedAt?.trim()) &&
        Boolean(input.resolvedPreviewVersionId?.trim()) &&
        input.resolvedPreviewVersionId.length === 64 &&
        input.resolutionMatchesCurrentPassingArtifact === true);
}
function hasCorruptResolvedBinding(input) {
    if (input.status !== "RESOLVED")
        return false;
    if (!input.resolvedAt?.trim())
        return true;
    if (!input.resolvedPreviewVersionId?.trim() || input.resolvedPreviewVersionId.length !== 64) {
        return true;
    }
    if (input.resolutionMatchesCurrentPassingArtifact !== true)
        return true;
    return false;
}
function agencySubmitted(input) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "AGENCY",
        journeyStage: "Change request",
        condition: "READY",
        title: "Start work on the submitted change request",
        reason: "The client submitted a change request. Agency work has not started yet.",
        primaryAction: {
            intent: "OPEN_AGENCY_CHANGE_REQUEST_WORK",
            projectId: input.projectId,
            changeRequestId: input.changeRequestId,
            label: "Start change work",
        },
        secondaryAction: null,
        waitingOn: null,
        blockers: [],
        progress: null,
        expectedResult: "Agency starts work on this exact request.",
        recovery: null,
        help: null,
        relevantRecords: [input.messagePreview?.trim() || "Submitted change request"],
        advancedDetails: null,
    });
}
function clientSubmitted(input) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "CLIENT",
        journeyStage: "Change request",
        condition: "WAITING",
        title: "Waiting for the agency to start",
        reason: "Your change request was saved. The agency has not started work yet.",
        primaryAction: null,
        secondaryAction: null,
        waitingOn: "Agency",
        blockers: [],
        progress: null,
        expectedResult: "The agency will start work on your request.",
        recovery: null,
        help: null,
        relevantRecords: [input.messagePreview?.trim() || "Your submitted change request"],
        advancedDetails: null,
    });
}
function agencyInProgress(input) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "AGENCY",
        journeyStage: "Change request",
        condition: "IN_PROGRESS",
        title: "Change work is in progress",
        reason: "Agency work is active on this exact change request. A revised preview is not yet bound.",
        primaryAction: {
            intent: "OPEN_AGENCY_CHANGE_REQUEST_WORK",
            projectId: input.projectId,
            changeRequestId: input.changeRequestId,
            label: "Continue change work",
        },
        secondaryAction: null,
        waitingOn: null,
        blockers: [],
        progress: null,
        expectedResult: "A revised preview will be bound to this exact request when ready.",
        recovery: null,
        help: null,
        relevantRecords: [input.messagePreview?.trim() || "In-progress change request"],
        advancedDetails: null,
    });
}
function clientInProgress(input) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "CLIENT",
        journeyStage: "Change request",
        condition: "IN_PROGRESS",
        title: "The agency is working on your request",
        reason: "Your agency is actively working on the requested changes.",
        primaryAction: null,
        secondaryAction: null,
        waitingOn: "Agency",
        blockers: [],
        progress: null,
        expectedResult: "A revised website preview will appear when the agency finishes this request.",
        recovery: null,
        help: null,
        relevantRecords: [input.messagePreview?.trim() || "Your change request"],
        advancedDetails: null,
    });
}
/** Client-safe human-readable reference for an exact previewVersionId (no internal hashes). */
export function formatClientSafeRevisedPreviewRef(previewVersionId) {
    return `Revised preview ${previewVersionId.slice(0, 12)}…`;
}
function agencyResolvedComplete(input) {
    const previewVersionId = input.resolvedPreviewVersionId;
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "AGENCY",
        journeyStage: "Change request",
        condition: "COMPLETE",
        title: "Change request resolved with a revised preview",
        reason: "This exact change request is bound to an exact revised preview. Client review is not yet complete.",
        primaryAction: null,
        secondaryAction: null,
        waitingOn: "Client",
        blockers: [],
        progress: null,
        expectedResult: "The client can review the exact revised preview.",
        recovery: null,
        help: null,
        relevantRecords: [formatClientSafeRevisedPreviewRef(previewVersionId)],
        advancedDetails: `Bound previewVersionId ${previewVersionId}`,
    });
}
function clientResolvedReady(input) {
    const previewVersionId = input.resolvedPreviewVersionId;
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "CLIENT",
        journeyStage: "Change request",
        condition: "READY",
        title: "Review the revised preview",
        reason: "Your agency finished the requested changes. A revised preview is ready for your review.",
        primaryAction: {
            intent: "OPEN_CLIENT_REVISED_PREVIEW",
            previewVersionId,
            label: "Open revised preview",
        },
        secondaryAction: null,
        waitingOn: null,
        blockers: [],
        progress: null,
        expectedResult: "You can review and approve or request further changes.",
        recovery: null,
        help: null,
        relevantRecords: [formatClientSafeRevisedPreviewRef(previewVersionId)],
        advancedDetails: null,
    });
}
function agencyNeedsAttention(input) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "AGENCY",
        journeyStage: "Change request",
        condition: "NEEDS_ATTENTION",
        title: "Change-request resolution needs attention",
        reason: "The request is marked resolved, but the exact revised-preview binding is missing, mismatched, or not current.",
        primaryAction: {
            intent: "OPEN_AGENCY_CHANGE_REQUEST_WORK",
            projectId: input.projectId,
            changeRequestId: input.changeRequestId,
            label: "Review change request",
        },
        secondaryAction: null,
        waitingOn: null,
        blockers: ["Exact revised-preview binding is incomplete or does not match the current passing preview"],
        progress: null,
        expectedResult: null,
        recovery: "Re-bind this exact request to the current passing revised preview from Agency change work.",
        help: null,
        relevantRecords: [input.messagePreview?.trim() || "Change request"],
        advancedDetails: null,
    });
}
function clientNeedsAttention(input) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        role: "CLIENT",
        journeyStage: "Change request",
        condition: "NEEDS_ATTENTION",
        title: "Revised preview is not ready to review",
        reason: "A revised preview could not be confirmed for this change request. Your saved request remains safe.",
        primaryAction: null,
        secondaryAction: null,
        waitingOn: "Agency",
        blockers: ["Revised preview confirmation is incomplete"],
        progress: null,
        expectedResult: null,
        recovery: "Wait for the agency to confirm the revised preview for this request.",
        help: null,
        relevantRecords: [input.messagePreview?.trim() || "Your change request"],
        advancedDetails: null,
    });
}
/**
 * Deterministic paired Agency/Client change-request presentation (ENR-UX-007).
 * Consumes authoritative lifecycle + binding outputs only. Never treats latestPreview
 * as resolution without resolutionMatchesCurrentPassingArtifact.
 */
export function derivePairedChangeRequestPresentation(input) {
    if (!idsAgree(input))
        return null;
    const status = parseStatus(input.status);
    if (!status) {
        return {
            agency: agencyNeedsAttention(input),
            client: clientNeedsAttention(input),
        };
    }
    // Latest preview alone never resolves.
    if (input.latestPreviewVersionId &&
        status !== "RESOLVED" &&
        input.resolutionMatchesCurrentPassingArtifact === true) {
        // Ignore false resolution flags on non-RESOLVED rows.
    }
    if (status === "SUBMITTED") {
        return { agency: agencySubmitted(input), client: clientSubmitted(input) };
    }
    if (status === "IN_PROGRESS") {
        return { agency: agencyInProgress(input), client: clientInProgress(input) };
    }
    if (hasCompleteMatchingResolution(input)) {
        return {
            agency: agencyResolvedComplete(input),
            client: clientResolvedReady(input),
        };
    }
    if (hasCorruptResolvedBinding(input) || status === "RESOLVED") {
        return {
            agency: agencyNeedsAttention(input),
            client: clientNeedsAttention(input),
        };
    }
    return {
        agency: agencyNeedsAttention(input),
        client: clientNeedsAttention(input),
    };
}
export function canonicalizeChangeRequestPresentation(model) {
    return stableStringify(model);
}
/** Fail-closed Client terminology scan for presentation copy. */
export function clientPresentationContainsProhibitedTerminology(model) {
    if (model.role !== "CLIENT")
        return false;
    const blob = [
        model.title,
        model.reason,
        model.expectedResult ?? "",
        model.recovery ?? "",
        model.help ?? "",
        model.advancedDetails ?? "",
        ...model.blockers,
        ...model.relevantRecords,
        model.primaryAction?.label ?? "",
    ].join("\n");
    return PROHIBITED_CLIENT_TERMS.some((term) => blob.includes(term));
}
//# sourceMappingURL=derive-change-request-presentation.js.map